<script setup>
import { ref, onMounted, watch } from 'vue'
import { get_backup_logs, create_backup } from '@/services/backup_logs.js'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'

const logs = ref([])
const loading = ref(false)
const creating = ref(false)
const currentPage = ref(1)
const totalPages = ref(1)
const expandedRows = ref(new Set())

const fetchLogs = async (page = 1) => {
  loading.value = true
  const res = await get_backup_logs({ page, per_page: 10 })
  loading.value = false
  
  if (!res.error) {
    logs.value = res.data.logs
    currentPage.value = res.data.meta.current_page
    totalPages.value = res.data.meta.total_pages
  }
}

const triggerBackup = async () => {
    creating.value = true
    const res = await create_backup()
    creating.value = false
    
    if (!res.error) {
        Swal.fire('Success', 'Backup started in background.', 'success')
        // Refresh logs after a short delay
        setTimeout(() => fetchLogs(), 1000)
    } else {
        Swal.fire('Error', 'Failed to start backup.', 'error')
    }
}

onMounted(() => {
  fetchLogs()
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    fetchLogs(page)
  }
}

// Format date helper
const formatDate = (date) => {
  return date ? dayjs(date).format('DD MMM YYYY, HH:mm:ss') : '-'
}

// Format file size
const formatSize = (sizeString) => {
  if (!sizeString) return '-'
  
  // Extract number from string like "2638872 bytes"
  const bytes = parseInt(sizeString.replace(/[^0-9]/g, ''))
  
  if (isNaN(bytes)) return sizeString
  
  if (bytes < 1024 * 1024) {
    // Less than 1 MB, show in KB
    return (bytes / 1024).toFixed(2) + ' KB'
  } else if (bytes < 1024 * 1024 * 1024) {
    // Less than 1 GB, show in MB
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
  } else {
    // Show in GB
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
  }
}

// Parse file names into array
const getFileList = (fileNameString) => {
  if (!fileNameString) return []
  return fileNameString.split(',').map(f => f.trim()).filter(f => f.length > 0)
}

// Toggle row expansion
const toggleRow = (logId) => {
  if (expandedRows.value.has(logId)) {
    expandedRows.value.delete(logId)
  } else {
    expandedRows.value.add(logId)
  }
}

const isExpanded = (logId) => {
  return expandedRows.value.has(logId)
}
</script>

<template>
  <div class="card shadow-sm">
    <div class="card-header bg-white d-flex justify-content-between align-items-center py-3">
      <div>
        <h5 class="mb-0"><i class="bi bi-cloud-arrow-up me-2"></i>Backup Logs</h5>
        <small class="text-muted">Automated database and storage backups</small>
      </div>
      <button class="btn btn-primary" @click="triggerBackup" :disabled="creating">
        <span v-if="creating" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        <i v-else class="bi bi-plus-circle me-2"></i>
        <span v-if="creating">Starting...</span>
        <span v-else>Create Backup</span>
      </button>
    </div>
    <div class="card-body p-0">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading backup logs...</p>
      </div>
      
      <div v-else class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th style="width: 5%"></th>
              <th style="width: 20%">Started At</th>
              <th style="width: 10%">Status</th>
              <th style="width: 10%">Type</th>
              <th style="width: 30%">Files</th>
              <th style="width: 15%">Size</th>
              <th style="width: 10%">Duration</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="log in logs" :key="log.id">
              <tr class="align-middle" style="cursor: pointer;" @click="toggleRow(log.id)">
                <td class="text-center">
                  <i :class="isExpanded(log.id) ? 'bi bi-chevron-down' : 'bi bi-chevron-right'"></i>
                </td>
                <td>
                  <div class="d-flex align-items-center">
                    <i class="bi bi-calendar3 text-muted me-2"></i>
                    <span>{{ formatDate(log.started_at) }}</span>
                  </div>
                </td>
                <td>
                  <span :class="{
                    'badge bg-success': log.status === 'success',
                    'badge bg-danger': log.status === 'failed',
                    'badge bg-warning text-dark': log.status === 'in_progress'
                  }">
                    <i :class="{
                      'bi bi-check-circle': log.status === 'success',
                      'bi bi-x-circle': log.status === 'failed',
                      'bi bi-hourglass-split': log.status === 'in_progress'
                    }" class="me-1"></i>
                    {{ log.status }}
                  </span>
                </td>
                <td>
                  <span class="badge bg-secondary">
                    <i class="bi bi-archive me-1"></i>
                    {{ log.backup_type }}
                  </span>
                </td>
                <td>
                  <span class="badge bg-info text-dark">
                    <i class="bi bi-files me-1"></i>
                    {{ getFileList(log.file_name).length }} file(s)
                  </span>
                </td>
                <td>
                  <span class="text-muted">
                    <i class="bi bi-hdd me-1"></i>
                    {{ formatSize(log.file_size) }}
                  </span>
                </td>
                <td>
                  <span v-if="log.ended_at && log.started_at" class="text-muted">
                    <i class="bi bi-clock me-1"></i>
                    {{ dayjs(log.ended_at).diff(dayjs(log.started_at), 'second') }}s
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
              <!-- Expanded row -->
              <tr v-if="isExpanded(log.id)" class="table-active">
                <td colspan="7" class="p-0">
                  <div class="p-3 bg-light">
                    <div class="row">
                      <div class="col-md-6">
                        <h6 class="mb-2"><i class="bi bi-file-earmark-text me-2"></i>Files Backed Up:</h6>
                        <ul class="list-unstyled mb-0">
                          <li v-for="(file, index) in getFileList(log.file_name)" :key="index" class="mb-1">
                            <i class="bi bi-file-earmark-zip text-primary me-2"></i>
                            <code class="text-dark">{{ file }}</code>
                          </li>
                        </ul>
                      </div>
                      <div class="col-md-6" v-if="log.log_message && log.log_message.trim().length > 0">
                        <h6 class="mb-2"><i class="bi bi-terminal me-2"></i>Log Details:</h6>
                        <pre class="bg-white p-3 rounded border" style="font-size: 0.85rem; max-height: 300px; overflow-y: auto; white-space: pre-wrap; color: #333;">{{ log.log_message }}</pre>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="logs.length === 0">
              <td colspan="7" class="text-center py-5">
                <i class="bi bi-inbox display-4 text-muted"></i>
                <p class="mt-3 text-muted">No backup logs found.</p>
                <button class="btn btn-outline-primary" @click="triggerBackup">Create Your First Backup</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center p-3 border-top" v-if="totalPages > 1">
        <span class="text-muted">Page {{ currentPage }} of {{ totalPages }}</span>
        <nav aria-label="Page navigation">
          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
                <i class="bi bi-chevron-left"></i> Previous
              </a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
                Next <i class="bi bi-chevron-right"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table tbody tr {
  transition: background-color 0.2s ease;
}

.table tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.card {
  border: none;
}
</style>
